/**
 * Verified email-sender configuration for LegacyGrowth Engine.
 *
 * The auth-email-hook (Supabase Auth) and every transactional Edge Function
 * MUST send from the same verified sender. Mixing verified and unverified
 * domains causes silent drops at major mailbox providers (Gmail, Outlook,
 * Yahoo) because of DMARC alignment failures.
 *
 * The verified domain is `legacygrowthinc.com`. The branded subdomain
 * `accounts.legacygrowthinc.com` is delegated to the platform's nameservers
 * for SPF / DKIM record management.
 */

const senderName = import.meta.env.VITE_EMAIL_SENDER_NAME || "LegacyGrowth Engine";
const senderDomain = import.meta.env.VITE_EMAIL_SENDER_DOMAIN || "example.com";
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || "support@example.com";

export const SENDER_NAME = senderName;
export const SENDER_EMAIL = `noreply@${senderDomain}`;
export const REPLY_TO = supportEmail;
export const SENDER_DOMAIN = senderDomain;
export const FROM_HEADER = `${SENDER_NAME} <${SENDER_EMAIL}>`;

/**
 * Required DNS records for inbox deliverability. These must be present at
 * the apex (`legacygrowthinc.com`) AND on the delegated subdomain
 * (`accounts.legacygrowthinc.com`). Documented here so the deployment doc
 * (added in a later phase) and human operators have a single source of truth.
 */
export const DNS_RECORDS = {
  spf: {
    type: "TXT",
    host: "@",
    value: "v=spf1 include:resend.com ~all",
    notes:
      "Authorizes Resend's MTAs to send mail as legacygrowthinc.com. " +
      "Do not add a second SPF record — flatten any existing one instead.",
  },
  dkim: {
    type: "CNAME",
    host: "resend._domainkey",
    value: "(provided by Resend after domain verification)",
    notes:
      "Publish the exact CNAME shown in Resend → Domains. DKIM signing is " +
      "required for DMARC alignment.",
  },
  dmarc: {
    type: "TXT",
    host: "_dmarc",
    value: "v=DMARC1; p=none; rua=mailto:dmarc@legacygrowthinc.com",
    notes:
      "Start with p=none to monitor; tighten to p=quarantine then p=reject " +
      "after a few weeks of clean reports.",
  },
} as const;

/**
 * Helper: returns the canonical From header. Use this in every Resend call.
 */
export function fromHeader(): string {
  return FROM_HEADER;
}